/**
 * Assistant Les Bornées · widget de chat pour la boutique Shopify
 *
 * Intégration dans le thème (voir README) :
 *   <script>
 *     window.LesBorneesAssistant = {
 *       endpoint: "https://assistant-les-bornees.onrender.com",
 *       isCustomer: {% if customer %}true{% else %}false{% endif %}
 *     };
 *   </script>
 *   <script src=".../lesbornees-assistant.js" defer></script>
 *
 * Autonome : aucune dépendance, styles injectés, accessible clavier,
 * responsive (plein écran sous 480 px).
 */
(function () {
  "use strict";

  var cfg = window.LesBorneesAssistant || {};
  var ENDPOINT = (cfg.endpoint || "").replace(/\/$/, "");
  if (!ENDPOINT) {
    console.warn("[Les Bornées] endpoint manquant dans window.LesBorneesAssistant");
    return;
  }
  var IS_CUSTOMER = cfg.isCustomer === true;

  // Langue de l'interface : anglais si la page est en anglais (attribut lang du
  // <html>, repli sur la locale du navigateur). Emma adapte déjà sa conversation
  // à la langue du visiteur ; ici on ne traduit QUE les libellés fixes de l'UI.
  var EN = (function () {
    try {
      var lang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
      if (lang) return lang.indexOf("en") === 0;
      var nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
      return nav.indexOf("en") === 0;
    } catch (e) { return false; }
  })();
  // Renseigné par le thème quand la personne est connectée à son compte client
  var CUSTOMER = null;
  if (cfg.customer && typeof cfg.customer === "object" && cfg.customer.email) {
    CUSTOMER = {
      email: String(cfg.customer.email).slice(0, 200),
      prenom: String(cfg.customer.prenom || "").slice(0, 60),
    };
  }

  // ------------------------------------------------------------------ état
  var store = {
    get: function (k, d) {
      try { return localStorage.getItem("lbchat_" + k) || d; } catch (e) { return d; }
    },
    set: function (k, v) {
      try { localStorage.setItem("lbchat_" + k, v); } catch (e) {}
    },
  };

  var sessionId = store.get("id", null);
  if (!sessionId) {
    sessionId = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    store.set("id", sessionId);
  }
  var tutoiement = IS_CUSTOMER ? "tu" : store.get("tuvous", null); // "tu" | "vous" | null
  var history = []; // {role, content} · mémoire de session (non persistée)
  var busy = false;
  var opened = false;

  // ------------------------------------------------------------------ styles
  var css = [
    ':root{--lb-violine:#901e48;--lb-violine-f:#6e1436;--lb-corail:#eb5e4b;--lb-orange:#df7a0c;--lb-creme:#fbf7f0;--lb-ink:#3c1f2c;--lb-ink-soft:#7a5a66;--lb-bord:#ecdccf}',
    '.lb-widget,.lb-widget *{box-sizing:border-box;font-family:"Montserrat",-apple-system,"Segoe UI",sans-serif}',
    '.lb-bulle{position:fixed;bottom:20px;right:20px;z-index:2147483000;width:60px;height:60px;border-radius:50%;border:none;cursor:pointer;background:linear-gradient(135deg,var(--lb-violine),var(--lb-violine-f));box-shadow:0 10px 26px rgba(144,30,72,.45);display:flex;align-items:center;justify-content:center;transition:transform .2s}',
    '.lb-bulle:hover{transform:scale(1.06)}.lb-bulle:focus-visible{outline:3px solid var(--lb-corail);outline-offset:2px}',
    '.lb-bulle svg{width:28px;height:28px}',
    '.lb-badge{position:absolute;top:-2px;right:-2px;width:14px;height:14px;border-radius:50%;background:var(--lb-corail);border:2px solid #fff}',
    '.lb-teaser{position:fixed;bottom:36px;right:92px;z-index:2147483000;background:#fff;color:var(--lb-ink);border:1px solid var(--lb-bord);border-radius:14px;border-bottom-right-radius:4px;padding:9px 14px;font-size:13px;box-shadow:0 6px 18px rgba(60,10,30,.15);max-width:230px}',
    '.lb-panel{position:fixed;bottom:92px;right:20px;z-index:2147483001;width:380px;max-width:calc(100vw - 24px);height:600px;max-height:calc(100vh - 110px);background:var(--lb-creme);border-radius:20px;box-shadow:0 24px 60px rgba(60,10,30,.3);display:flex;flex-direction:column;overflow:hidden}',
    '@media(max-width:480px){.lb-panel{bottom:0;right:0;width:100vw;max-width:100vw;height:100%;max-height:100%;border-radius:0}}',
    '.lb-head{background:linear-gradient(135deg,var(--lb-violine),var(--lb-violine-f));color:#fff;padding:14px 16px;display:flex;align-items:center;gap:10px;flex-shrink:0}',
    '.lb-head-title{flex:1;min-width:0}',
    '.lb-ava{width:40px;height:40px;flex-shrink:0;border-radius:50%;overflow:hidden;box-shadow:0 2px 6px rgba(0,0,0,.2)}',
    '.lb-ava svg{display:block;width:100%;height:100%}',
    '.lb-ava img{display:block;width:100%;height:100%;object-fit:cover}',
    '.lb-title{font-size:14px;font-weight:800;display:block;line-height:1.25}',
    '.lb-script{font-family:"Snell Roundhand","Savoye LET","Brush Script MT",cursive;font-size:22px;line-height:1.1;display:block}',
    '.lb-head small{font-size:11px;opacity:.85;display:flex;align-items:center;gap:6px;font-weight:600}',
    '.lb-dot{width:7px;height:7px;border-radius:50%;background:#7ce0a3}',
    '.lb-close{margin-left:auto;background:rgba(255,255,255,.15);border:none;color:#fff;width:30px;height:30px;border-radius:50%;font-size:15px;cursor:pointer;flex-shrink:0}',
    '.lb-close:focus-visible{outline:2px solid #fff;outline-offset:1px}',
    '.lb-body{flex:1;overflow-y:auto;padding:14px 12px;display:flex;flex-direction:column;gap:10px;font-size:13.5px;color:var(--lb-ink)}',
    '.lb-body>*{flex-shrink:0}',
    '.lb-msg{max-width:86%;padding:9px 13px;border-radius:15px;line-height:1.5;white-space:pre-wrap;word-wrap:break-word}',
    '.lb-msg.bot{background:#fff;border:1px solid var(--lb-bord);border-bottom-left-radius:4px;align-self:flex-start}',
    '.lb-msg.user{background:var(--lb-violine);color:#fff;border-bottom-right-radius:4px;align-self:flex-end}',
    '.lb-msg a{color:var(--lb-corail);font-weight:700;text-decoration:underline}',
    '.lb-msg.user a{color:#fff}',
    '.lb-typing{align-self:flex-start;color:var(--lb-ink-soft);font-size:12px;padding:2px 6px}',
    '.lb-chips{display:flex;flex-wrap:wrap;gap:7px;align-self:flex-start}',
    '.lb-chip{background:#fff;border:1.5px solid var(--lb-corail);color:var(--lb-corail);border-radius:999px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit}',
    '.lb-chip:hover,.lb-chip:focus-visible{background:var(--lb-corail);color:#fff;outline:none}',
    '.lb-card{align-self:flex-start;width:88%;background:#fff;border:1px solid var(--lb-bord);border-radius:15px;overflow:hidden}',
    '.lb-card-img{height:90px;background:linear-gradient(120deg,var(--lb-orange),var(--lb-corail));background-size:cover;background-position:center}',
    '.lb-card-in{padding:11px 13px}',
    '.lb-card-in h4{margin:0 0 3px;font-size:13.5px;font-weight:800;color:var(--lb-ink)}',
    '.lb-card-in p{margin:0 0 9px;font-size:12px;color:var(--lb-ink-soft)}',
    '.lb-btn{display:inline-block;border:none;border-radius:999px;padding:8px 15px;font-family:inherit;font-size:12.5px;font-weight:800;cursor:pointer;text-decoration:none;text-align:center}',
    '.lb-btn.corail{background:var(--lb-corail);color:#fff}',
    '.lb-btn.wa{background:#1fb355;color:#fff}',
    '.lb-btn.cal{background:var(--lb-violine);color:#fff}',
    '.lb-btn.orange{background:var(--lb-orange);color:#fff}',
    '.lb-module{align-self:flex-start;width:88%;background:#fff;border:1px solid var(--lb-bord);border-radius:15px;padding:12px 13px}',
    '.lb-module p{margin:0 0 9px;font-size:12.5px}',
    '.lb-module .lb-row{display:flex;gap:8px;flex-wrap:wrap}',
    '.lb-module input[type=email]{width:100%;border:1.5px solid var(--lb-bord);border-radius:10px;padding:9px 12px;font-family:inherit;font-size:13px;background:var(--lb-creme);color:var(--lb-ink)}',
    '.lb-module input[type=email]:focus{outline:2px solid var(--lb-corail);border-color:var(--lb-corail)}',
    '.lb-consent{display:flex;gap:8px;align-items:flex-start;font-size:11px;color:var(--lb-ink-soft);margin:9px 0}',
    '.lb-consent input{accent-color:var(--lb-violine);margin-top:2px;flex-shrink:0}',
    '.lb-foot{border-top:1px solid var(--lb-bord);background:#fff;padding:10px 12px 7px;flex-shrink:0}',
    '.lb-in-row{display:flex;gap:8px;align-items:center}',
    '.lb-input{flex:1;border:1.5px solid var(--lb-bord);border-radius:999px;padding:10px 14px;font-family:inherit;font-size:13px;background:var(--lb-creme);color:var(--lb-ink)}',
    '.lb-input:focus{outline:2px solid var(--lb-corail);border-color:var(--lb-corail)}',
    '.lb-send{width:40px;height:40px;border-radius:50%;border:none;background:var(--lb-violine);color:#fff;font-size:15px;cursor:pointer;flex-shrink:0}',
    '.lb-send:disabled{opacity:.5;cursor:default}',
    '.lb-legal{margin:6px 2px 0;font-size:9.5px;color:var(--lb-ink-soft);text-align:center}',
    '.lb-legal a{color:var(--lb-violine)}',
    '@media(prefers-reduced-motion:reduce){.lb-bulle{transition:none}}',
  ].join("\n");

  var style = document.createElement("style");
  style.textContent = css;
  document.head.appendChild(style);

  // ------------------------------------------------------------------ DOM
  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === "text") n.textContent = attrs[k];
      else if (k === "html") n.innerHTML = attrs[k];
      else n.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { n.appendChild(c); });
    return n;
  }

  // Avatar mascotte : frimousse au casque de vélo, aux couleurs de la charte.
  var AVATAR_SVG =
    '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="24" cy="24" r="24" fill="#fbf7f0"/>' +
    '<circle cx="13.2" cy="35.5" r="2.6" fill="#eb5e4b" opacity=".38"/>' +
    '<circle cx="34.8" cy="35.5" r="2.6" fill="#eb5e4b" opacity=".38"/>' +
    '<circle cx="18" cy="31.8" r="2.2" fill="#3c1f2c"/>' +
    '<circle cx="30" cy="31.8" r="2.2" fill="#3c1f2c"/>' +
    '<path d="M18 37 Q24 42 30 37" stroke="#3c1f2c" stroke-width="2.3" stroke-linecap="round" fill="none"/>' +
    '<path d="M8 24 A16 16 0 0 1 40 24 L40 24.6 L8 24.6 Z" fill="#901e48"/>' +
    '<rect x="6.8" y="24" width="34.4" height="3.4" rx="1.7" fill="#6e1436"/>' +
    '<path d="M24 9.4 L24 19.5" stroke="#eb5e4b" stroke-width="2.6" stroke-linecap="round"/>' +
    '<path d="M16.6 11.6 L19.2 20" stroke="#eb5e4b" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="M31.4 11.6 L28.8 20" stroke="#eb5e4b" stroke-width="2.4" stroke-linecap="round"/>' +
    "</svg>";

  var root = el("div", { class: "lb-widget" });

  var bulle = el("button", {
    class: "lb-bulle",
    "aria-label": EN ? "Open the Les Bornées assistant" : "Ouvrir l'assistant Les Bornées",
    "aria-expanded": "false",
    html:
      '<span class="lb-badge" aria-hidden="true"></span>' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3 8.9 8.9 0 0 1-3.2-.6L4 21l1.9-4.6a8.1 8.1 0 0 1-1.4-4.9A8.4 8.4 0 0 1 13 3.2a8.4 8.4 0 0 1 8 8.3z"/></svg>',
  });
  root.appendChild(bulle);

  var panel = null, body = null, input = null, sendBtn = null;

  function buildPanel() {
    body = el("div", { class: "lb-body", "aria-live": "polite" });
    input = el("input", {
      class: "lb-input", type: "text",
      placeholder: EN ? "Type your question..." : "Écris ta question...",
      "aria-label": EN ? "Your question" : "Ta question",
      maxlength: "1000",
    });
    sendBtn = el("button", { class: "lb-send", "aria-label": EN ? "Send" : "Envoyer", text: "➤" });

    var foot = el("div", { class: "lb-foot" }, [
      el("div", { class: "lb-in-row" }, [input, sendBtn]),
      el("p", {
        class: "lb-legal",
        html: EN
          ? 'AI assistant · no sensitive data stored · <a href="https://lesbornees.com/pages/contact" target="_blank" rel="noopener">contact</a>'
          : 'Assistant IA · aucune donnée sensible conservée · <a href="https://lesbornees.com/pages/contact" target="_blank" rel="noopener">contact</a>',
      }),
    ]);

    // Avatar : photo si le serveur en propose une (widget/avatar.jpg),
    // sinon la mascotte dessinée en secours automatique.
    var ava = el("div", { class: "lb-ava", "aria-hidden": "true" });
    var avaImg = el("img", { src: ENDPOINT + "/widget/avatar.jpg", alt: "" });
    avaImg.onerror = function () { ava.innerHTML = AVATAR_SVG; };
    ava.appendChild(avaImg);

    var head = el("div", { class: "lb-head" }, [
      ava,
      el("div", { class: "lb-head-title" }, [
        el("strong", { class: "lb-title", text: "Emma · Les Bornées" }),
        el("small", { html: '<span class="lb-dot"></span> ' + (EN ? "Virtual assistant · live" : "Assistante virtuelle · en direct") }),
      ]),
      el("button", { class: "lb-close", "aria-label": EN ? "Close the assistant" : "Fermer l'assistant", text: "✕" }),
    ]);
    head.querySelector(".lb-close").addEventListener("click", togglePanel);

    panel = el("div", {
      class: "lb-panel", role: "dialog",
      "aria-label": "Assistant Les Bornées", "aria-modal": "false",
    }, [head, body, foot]);
    root.appendChild(panel);

    sendBtn.addEventListener("click", function () { submit(input.value); });
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.keyCode === 13) submit(input.value);
    });
    panel.addEventListener("keydown", function (e) {
      if (e.key === "Escape") togglePanel();
    });

    welcome();
  }

  // ------------------------------------------------------------------ rendu
  function scrollDown() { body.scrollTop = body.scrollHeight; }

  function esc(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  // Markdown minimal et sûr : liens + gras, sur texte échappé.
  function renderMd(s) {
    var h = esc(s);
    h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener">$1</a>');
    h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return h;
  }

  function addMsg(role, text) {
    var m = el("div", { class: "lb-msg " + role });
    m.innerHTML = renderMd(text);
    body.appendChild(m);
    scrollDown();
    return m;
  }

  function addChips(items) {
    var wrap = el("div", { class: "lb-chips", role: "group", "aria-label": EN ? "Suggestions" : "Suggestions" });
    items.forEach(function (it) {
      var c = el("button", { class: "lb-chip", text: it.label });
      c.addEventListener("click", function () {
        if (it.pref) { tutoiement = it.pref; store.set("tuvous", it.pref); }
        wrap.remove();
        submit(it.send || it.label);
      });
      wrap.appendChild(c);
    });
    body.appendChild(wrap);
    scrollDown();
  }

  function addCards(cards) {
    cards.forEach(function (c) {
      var img = el("div", { class: "lb-card-img", "aria-hidden": "true" });
      if (c.image) img.style.backgroundImage = "url('" + c.image.replace(/'/g, "") + "')";
      var prix = c.prix_min ? (EN ? "From " : "À partir de ") + formatPrix(c.prix_min) : "";
      var card = el("div", { class: "lb-card" }, [
        img,
        el("div", { class: "lb-card-in" }, [
          el("h4", { text: c.titre }),
          el("p", {
            text: prix + (c.precommande
              ? (EN ? " · pre-registrations open" : " · pré-inscriptions ouvertes")
              : c.disponible === false
                ? (EN ? " · online booking closed" : " · réservation en ligne fermée")
                : ""),
          }),
          el("a", { class: "lb-btn corail", href: c.url, target: "_blank", rel: "noopener", text: EN ? "View the trip" : "Voir le séjour" }),
        ]),
      ]);
      body.appendChild(card);
    });
    scrollDown();
  }

  function formatPrix(p) {
    var m = String(p).match(/^([\d.]+)\s*EUR/);
    if (!m) return p;
    var n = parseFloat(m[1]);
    return (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2).replace(".", ",")) + " €";
  }

  function addEscalade() {
    var mod = el("div", { class: "lb-module" }, [
      el("p", { text: EN ? "A real person from the team is here for that:" : "Une vraie personne de la team est là pour ça :" }),
      el("div", { class: "lb-row" }, [
        el("a", { class: "lb-btn wa", href: "https://wa.me/33630431356", target: "_blank", rel: "noopener", text: "💬 WhatsApp" }),
        el("a", { class: "lb-btn cal", href: "https://calendly.com/maude-baudier/question-sur-un-stage-les-bornees", target: "_blank", rel: "noopener", text: EN ? "📅 Book a call" : "📅 Prendre RDV" }),
      ]),
    ]);
    body.appendChild(mod);
    scrollDown();
  }

  function addEmailCard(interet) {
    var emailInput = el("input", { type: "email", placeholder: EN ? "you@email.com" : "ton@email.com", "aria-label": EN ? "Your email" : "Ton e-mail" });
    if (CUSTOMER) emailInput.value = CUSTOMER.email;
    var check = el("input", { type: "checkbox", id: "lb-consent-" + Date.now() });
    var label = el("label", { class: "lb-consent", for: check.id }, [
      check,
      el("span", { text: EN
        ? "I agree that Les Bornées may contact me about my enquiry. You can unsubscribe at any time."
        : "J'accepte que Les Bornées me recontacte à propos de ma demande. Désinscription possible à tout moment." }),
    ]);
    var btn = el("button", { class: "lb-btn orange", text: EN ? "Contact me" : "Me recontacter" });
    var mod = el("div", { class: "lb-module" }, [
      el("p", { html: EN ? "<strong>Shall we get back to you?</strong>" : "<strong>On te recontacte ?</strong>" }),
      emailInput, label, btn,
    ]);
    btn.addEventListener("click", function () {
      if (!check.checked) { label.style.color = "#c0392b"; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailInput.value)) {
        emailInput.style.borderColor = "#c0392b";
        return;
      }
      btn.disabled = true;
      btn.textContent = EN ? "Sending..." : "Envoi...";
      var lastUser = "";
      for (var i = history.length - 1; i >= 0; i--) {
        if (history[i].role === "user") { lastUser = history[i].content; break; }
      }
      fetch(ENDPOINT + "/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailInput.value, consent: true, note: lastUser, interet: interet || null, sessionId: sessionId }),
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
        .then(function () {
          mod.innerHTML = EN
            ? "<p><strong>All noted!</strong> Maude will get back to you soon. 🙂</p>"
            : "<p><strong>C'est noté !</strong> Maude revient vers toi rapidement. 🙂</p>";
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = EN ? "Contact me" : "Me recontacter";
          mod.appendChild(el("p", { text: EN
            ? "Oops, sending failed. Please try again or reach us on WhatsApp."
            : "Oups, l'envoi n'a pas fonctionné. Réessaie ou passe par WhatsApp." }));
        });
    });
    body.appendChild(mod);
    scrollDown();
  }

  // ------------------------------------------------------------------ accueil
  function welcome() {
    if (EN) {
      // En anglais, la distinction tu/vous n'existe pas : accueil direct.
      addMsg("bot", IS_CUSTOMER
        ? "Hi there! 👋 Lovely to see you again. I'm Emma, the Les Bornées virtual assistant: trips, clothing, e-books, planning... ask me anything."
        : "Hi there! 👋 I'm Emma, the Les Bornées virtual assistant. Sports trips, clothing, e-books, planning: I'm here to help. What can I do for you?");
      addChips(standardChips());
      return;
    }
    if (IS_CUSTOMER) {
      addMsg("bot", "Salut ! 👋 Contente de te revoir par ici. Moi c'est Emma, l'assistante virtuelle Les Bornées : séjours, textile, e-books, organisation... dis-moi tout.");
      addChips(standardChips());
    } else if (!tutoiement) {
      addMsg("bot", "Salut ! 👋 Moi c'est Emma, l'assistante virtuelle Les Bornées. Séjours sportifs, textile, e-books, organisation : je suis là pour répondre. Au fait, on se tutoie ou on se vouvoie ?");
      addChips([
        { label: "On se tutoie 🙂", pref: "tu", send: "On se tutoie !" },
        { label: "Plutôt vouvoiement", pref: "vous", send: "Je préfère le vouvoiement." },
      ]);
    } else {
      addMsg("bot", tutoiement === "vous"
        ? "Bonjour ! 👋 Je suis Emma, l'assistante virtuelle Les Bornées. Que puis-je faire pour vous ?"
        : "Salut ! 👋 Moi c'est Emma, l'assistante virtuelle Les Bornées. Qu'est-ce que je peux faire pour toi ?");
      addChips(standardChips());
    }
  }

  function standardChips() {
    if (EN) {
      return [
        { label: "Find my trip", send: "Help me find the trip that suits me." },
        { label: "Coming on my own", send: "I don't know anyone, will I fit in?" },
        { label: "Pay in instalments", send: "How does paying in instalments work?" },
        { label: "Clothing sizes", send: "How do I choose my size for the clothing?" },
      ];
    }
    return [
      { label: "Trouver mon séjour", send: "Aide-moi à trouver le séjour qui me correspond." },
      { label: "Venir sans connaître personne", send: "Je ne connais personne, est-ce que je vais m'intégrer ?" },
      { label: "Payer en plusieurs fois", send: "Comment fonctionne le paiement en plusieurs fois ?" },
      { label: "Tailles textile", send: "Comment choisir ma taille pour le textile ?" },
    ];
  }

  // ------------------------------------------------------------------ envoi
  function submit(text) {
    text = (text || "").trim();
    if (!text || busy) return;
    input.value = "";
    busy = true;
    sendBtn.disabled = true;

    addMsg("user", text);
    history.push({ role: "user", content: text });

    var typing = el("div", { class: "lb-typing", text: EN ? "typing..." : "écrit..." });
    body.appendChild(typing);
    scrollDown();

    var botMsg = null;
    var botText = "";

    fetch(ENDPOINT + "/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        messages: history.slice(-30),
        session: {
          id: sessionId,
          tutoiement: tutoiement,
          isCustomer: IS_CUSTOMER,
          customer: CUSTOMER,
          page: location.pathname,
        },
      }),
    })
      .then(function (res) {
        if (!res.ok || !res.body) throw new Error("HTTP " + res.status);
        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        var buffer = "";

        function handleEvent(evt, data) {
          if (evt === "text" && data.delta) {
            if (typing.parentNode) typing.remove();
            if (!botMsg) botMsg = addMsg("bot", "");
            botText += data.delta;
            botMsg.innerHTML = renderMd(botText);
            scrollDown();
          } else if (evt === "cards") {
            addCards(data.cards || []);
          } else if (evt === "escalade") {
            addEscalade();
          } else if (evt === "email_card") {
            addEmailCard(data.interet);
          } else if (evt === "error") {
            if (typing.parentNode) typing.remove();
            addMsg("bot", data.message || (EN ? "Oops, a small technical glitch. Please try again in a moment." : "Oups, petit souci technique. Réessaie dans un instant."));
          }
        }

        function pump() {
          return reader.read().then(function (r) {
            if (r.done) return;
            buffer += decoder.decode(r.value, { stream: true });
            var blocks = buffer.split("\n\n");
            buffer = blocks.pop();
            blocks.forEach(function (block) {
              var evt = null, data = "";
              block.split("\n").forEach(function (line) {
                if (line.indexOf("event: ") === 0) evt = line.slice(7);
                else if (line.indexOf("data: ") === 0) data = line.slice(6);
              });
              if (evt) {
                try { handleEvent(evt, data ? JSON.parse(data) : {}); } catch (e) {}
              }
            });
            return pump();
          });
        }
        return pump();
      })
      .catch(function () {
        if (typing.parentNode) typing.remove();
        addMsg("bot", EN
          ? "Oops, I can't reply right now. Please try again in a moment, or message the team on [WhatsApp](https://wa.me/33630431356)."
          : "Oups, je n'arrive pas à répondre pour le moment. Réessaie dans un instant, ou écris à l'équipe sur [WhatsApp](https://wa.me/33630431356).");
      })
      .then(function () {
        if (typing.parentNode) typing.remove();
        if (botText) history.push({ role: "assistant", content: botText });
        busy = false;
        sendBtn.disabled = false;
        input.focus();
      });
  }

  // ------------------------------------------------------------------ ouverture
  var teaser = null;

  function togglePanel() {
    opened = !opened;
    bulle.setAttribute("aria-expanded", String(opened));
    if (opened) {
      if (teaser) { teaser.remove(); teaser = null; }
      var badge = bulle.querySelector(".lb-badge");
      if (badge) badge.remove();
      if (!panel) buildPanel();
      panel.style.display = "flex";
      input.focus();
    } else if (panel) {
      panel.style.display = "none";
      bulle.focus();
    }
  }

  bulle.addEventListener("click", togglePanel);
  document.body.appendChild(root);

  // Sur mobile, le menu burger et le panier coulissant recouvrent l'écran :
  // on efface tout le widget tant qu'ils sont ouverts, et on le réaffiche après.
  function overlayOuvert() {
    try {
      var md = document.getElementById("menu-drawer");
      if (md) {
        var det = md.closest("details");
        if (det && det.hasAttribute("open")) return true;
      }
      if (document.querySelector('details[id*="menu-drawer" i][open]')) return true;
      var cd = document.querySelector("cart-drawer");
      if (cd && (cd.classList.contains("active") || cd.classList.contains("animate"))) return true;
      if (document.querySelector("details-modal details[open]")) return true;
    } catch (e) {}
    return false;
  }
  setInterval(function () {
    root.style.display = overlayOuvert() ? "none" : "";
  }, 400);

  // Accroche discrète après 6 s (une fois par session de navigation)
  try {
    if (!sessionStorage.getItem("lbchat_teased")) {
      setTimeout(function () {
        if (opened) return;
        teaser = el("div", { class: "lb-teaser", text: EN ? "A question about the trips? Emma's here 🙂" : "Une question sur les séjours ? Emma est là 🙂" });
        root.appendChild(teaser);
        sessionStorage.setItem("lbchat_teased", "1");
        setTimeout(function () { if (teaser) { teaser.remove(); teaser = null; } }, 8000);
      }, 6000);
    }
  } catch (e) {}
})();
